// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Chat Service
 *
 * Handles SSE streaming communication with the BFF AI chat endpoint.
 * streamChat uses raw fetch + ReadableStream (SSE can't use the JSON api wrapper).
 * Session listing uses the api.ts wrapper (standard JSON requests).
 *
 * Sends page context (currentPage, siteId) and session_id alongside
 * messages so the agent proxy can inject context and maintain threads.
 */

import { getApiUrl, ENDPOINTS } from '../utils/constants';
import { get } from './api';
import { getTokenAsync } from '../config/authConfig';
import { removeUserData } from './tokenStorage';
import { useShellStore } from '../stores/shellStore';
import type {
  ChatContextPanelEvent,
  ChatRequestPayload,
  ChatSession,
  ChatSessionMessage,
  ChatStreamEvent,
} from '../types';

/**
 * Stream a chat response from the BFF.
 *
 * @param payload  - Full request payload including messages, context, and session
 * @param onChunk  - Called for each content chunk (token) received
 * @param onError  - Called if an error occurs (network or model error)
 * @param onDone   - Called when the stream completes
 * @param onSessionId - Called when a session_id event is received (for thread continuity)
 * @param signal   - Optional AbortSignal for cancellation
 */
export async function streamChat(
  payload: ChatRequestPayload,
  onChunk: (_content: string) => void,
  onError: (_error: string) => void,
  onDone: () => void,
  onSessionId?: (_sessionId: string) => void,
  signal?: AbortSignal,
  onContextPanel?: (_panel: ChatContextPanelEvent) => void
): Promise<void> {
  const url = getApiUrl(ENDPOINTS.AI_CHAT);

  let token: string;
  try {
    token = await getTokenAsync();
  } catch {
    // Couldn't acquire a token (e.g. silent refresh not ready). This is not a
    // confirmed expiry — getTokenAsync already redirects to CIAM when
    // interaction is genuinely required. Surface a chat error instead of
    // nuking the session, which previously caused a session-expired loop.
    onError('Not authenticated. Please try again.');
    onDone();
    return;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    onError('Failed to connect to the AI service.');
    onDone();
    return;
  }

  if (response.status === 401) {
    removeUserData();
    useShellStore.getState().setSessionExpired(true);
    return;
  }

  if (!response.ok) {
    onError(`AI service error (${response.status})`);
    onDone();
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError('Streaming not supported by browser.');
    onDone();
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE lines from the buffer
      const lines = buffer.split('\n');
      // Keep the last potentially incomplete line in the buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6); // remove "data: "

        if (data === '[DONE]') {
          onDone();
          return;
        }

        try {
          const event: ChatStreamEvent = JSON.parse(data);
          if (event.error) {
            onError(event.error);
          } else {
            if (event.session_id && onSessionId) {
              onSessionId(event.session_id);
            }
            if (event.context_panel && onContextPanel) {
              onContextPanel(event.context_panel);
            }
            if (event.content) {
              onChunk(event.content);
            }
          }
        } catch {
          // Skip malformed SSE lines
        }
      }
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    onError('Connection to AI service lost.');
  }

  onDone();
}

/**
 * Fetch the list of chat sessions from the BFF.
 * Uses the api.ts wrapper for auth, error handling, and JSON parsing.
 */
export async function fetchSessions(): Promise<ChatSession[]> {
  try {
    return await get<ChatSession[]>(ENDPOINTS.AI_SESSIONS);
  } catch (error) {
    console.error('Failed to fetch chat sessions:', error);
    return [];
  }
}

/**
 * Fetch messages for a specific session from the BFF.
 * Uses the api.ts wrapper for auth, error handling, and JSON parsing.
 */
export async function fetchSessionMessages(sessionId: string): Promise<ChatSessionMessage[]> {
  try {
    return await get<ChatSessionMessage[]>(ENDPOINTS.AI_SESSION_MESSAGES(sessionId));
  } catch (error) {
    console.error('Failed to fetch session messages for session', sessionId, error);
    throw error;
  }
}
